class AccordionItem {
  constructor(itemElement, animationSpeed = 300) {
    this.item = itemElement;
    this.toggleButton = this.item.querySelector('.accordion-item--header');
    this.inner = this.item.querySelector('.accordion-item--inner');
    this.animationSpeed = animationSpeed;

    if (this.toggleButton && this.inner) {
      this.setupAccessibility();
      
      this.toggleButton.addEventListener('click', (e) => {
        // Prevent toggle if the click was on an anchor tag inside the toggle zone
        if (e.target.closest('a')) return;

        this.toggle();
      });

      this.toggleButton.addEventListener('keydown', e => this.handleKeydown(e));
    }
  }

  setupAccessibility() {
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.toggleButton.setAttribute('aria-controls', this.inner.id || this.generateId(this.inner));
    this.inner.setAttribute('aria-hidden', 'true');

    if (!this.toggleButton.hasAttribute('role')) {
      this.toggleButton.setAttribute('role', 'button');
    }

    if (!this.inner.id) {
      this.inner.id = this.generateId(this.inner);
    }
  }

  generateId(element) {
    return 'accordion-' + Math.random().toString(36).substr(2, 9);
  }

  handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggle();
    }
  }

  toggle() {
    const isOpen = this.item.classList.contains('accordion-open');
    this.closeSiblings();

    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.item.classList.add('accordion-open');
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.inner.setAttribute('aria-hidden', 'false');
    this.slideDown(this.inner);
  }

  close() {
    this.item.classList.remove('accordion-open');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.inner.setAttribute('aria-hidden', 'true');
    this.slideUp(this.inner);

    const nestedItems = this.item.querySelectorAll('.accordion-item.accordion-open');
    nestedItems.forEach(nested => {
      nested.classList.remove('accordion-open');
      const nestedToggle = nested.querySelector('.accordion-item--toggle');
      const nestedInner = nested.querySelector('.accordion-item--inner');
      if (nestedToggle) nestedToggle.setAttribute('aria-expanded', 'false');
      if (nestedInner) {
        nestedInner.setAttribute('aria-hidden', 'true');
        this.slideUp(nestedInner);
      }
    });
  }

  closeSiblings() {
    const siblings = [...this.item.parentElement.children].filter(
      el => el !== this.item && el.classList.contains('accordion-item')
    );

    siblings.forEach(sibling => {
      sibling.classList.remove('accordion-open');
      const siblingToggle = sibling.querySelector('.accordion-item--toggle');
      const siblingInner = sibling.querySelector('.accordion-item--inner');
      if (siblingToggle) siblingToggle.setAttribute('aria-expanded', 'false');
      if (siblingInner) {
        siblingInner.setAttribute('aria-hidden', 'true');
        this.slideUp(siblingInner);
      }

      const nested = sibling.querySelectorAll('.accordion-item.accordion-open');
      nested.forEach(nestedItem => {
        nestedItem.classList.remove('accordion-open');
        const nestedToggle = nestedItem.querySelector('.accordion-item--toggle');
        const nestedInner = nestedItem.querySelector('.accordion-item--inner');
        if (nestedToggle) nestedToggle.setAttribute('aria-expanded', 'false');
        if (nestedInner) {
          nestedInner.setAttribute('aria-hidden', 'true');
          this.slideUp(nestedInner);
        }
      });
    });
  }

  slideDown(element) {
    element.style.height = '0px';
    element.style.display = 'block';
    const height = element.scrollHeight;
    element.style.transition = `height ${this.animationSpeed}ms ease`;
    requestAnimationFrame(() => {
      element.style.height = `${height}px`;
    });
    setTimeout(() => {
      element.style.height = '';
      element.style.transition = '';
    }, this.animationSpeed);
  }

  slideUp(element) {
    const height = element.scrollHeight;
    element.style.height = `${height}px`;
    element.style.transition = `height ${this.animationSpeed}ms ease`;
    requestAnimationFrame(() => {
      element.style.height = '0px';
    });
    setTimeout(() => {
      element.style.display = 'none';
      element.style.height = '';
      element.style.transition = '';
    }, this.animationSpeed);
  }
}

export { AccordionItem };
